'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'           // ← добавлено
import PremiumImageLoadFrame from '../components/PremiumImageLoadFrame'
import { useI18n } from '../components/i18n'
import HomeBetweenBlocksAd from './ads'
import CryptoNewsLens from './components/CryptoNewsLens'
import { SiteSocialLinksRow } from '../components/TopBar'
import FooterViewportPark from '../components/FooterViewportPark'
import FooterMarqueeTail from '../components/FooterMarqueeTail'
import { QuantumWalletLaunchButton } from '../components/QuantumWalletLaunchIcon'
import { runAuthorizedClientAction } from '../lib/authActionGateClient'

const HOME_OPERATION_MODULES = Object.freeze([
  { id: 'metamarket', code: 'MM', state: 'live', action: 'metamarket' },
  { id: 'forum', code: 'QF', state: 'live', href: '/forum' },
  { id: 'community', code: 'CM', state: 'live', href: '/forum?communityOpen=1&inbox=messages' },
  { id: 'exchange', code: 'EX', state: 'partial', href: '/exchange' },
  { id: 'aiBox', code: 'AI', state: 'live', href: '/exchange#ql7-exchange-ai-box' },
  { id: 'battleCoin', code: 'BC', state: 'live', href: '/exchange#ql7-exchange-battlecoin' },
  { id: 'battleChat', code: 'BT', state: 'live', href: '/exchange#ql7-exchange-battle-chat' },
  { id: 'ads', code: 'AD', state: 'live', href: '/ads' },
  { id: 'quests', code: 'QS', state: 'roadmap' },
  { id: 'academy', code: 'AC', state: 'live', href: '/academy' },
  { id: 'gameverse', code: 'GV', state: 'partial', href: '/game' },
  { id: 'quantumZigzag', code: 'QZ', state: 'roadmap' },
  { id: 'metaStudio', code: 'MS', state: 'roadmap', href: '/game?ql7Action=metastudio#metastudio' },
  { id: 'l7Blockchain', code: 'L7', state: 'longterm' },
  { id: 'quantumUniverse', code: 'QU', state: 'longterm' },
])

const HOME_BLOOMBERG_MEDIA_ID = 'home-bloomberg-live'
const HOME_BLOOMBERG_LIVE_URL =
  'https://www.bloomberg.com/live/us?autoplay=false&muted=true'
const MEDIA_MUTED_EVENT = 'forum:media-mute'
const SITE_MEDIA_PLAY_EVENT = 'site-media-play'

function AutoMedia({
  base,
  poster,
  // видео больше не используем, по умолчанию только картинки
  exts = ['jpg', 'png', 'webp'],
  className,
  alt = '',
  imgProps = {},
  videoProps = {},
  previewWidth = 0,
  previewHeight = 0,
  previewTheme = 'home',
}) {
  const [src, setSrc] = useState(null)
  // видео больше не рендерим – всегда картинка
  const [kind, setKind] = useState('img')

  useEffect(() => {
    let dead = false

    async function tryHead(url) {
      try {
        const controller = new AbortController()
        const t = setTimeout(() => controller.abort(), 3000)
        const r = await fetch(url, { method: 'HEAD', cache: 'no-store', signal: controller.signal })
        clearTimeout(t)
        if (r.ok) return true
      } catch {}
      return false
    }

    async function tryByte(url) {
      try {
        const controller = new AbortController()
        const t = setTimeout(() => controller.abort(), 3000)
        const r = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-0' },
          cache: 'no-store',
          signal: controller.signal,
        })
        clearTimeout(t)
        if (r.ok) return true
      } catch {}
      return false
    }

    async function pick() {
      for (const ext of exts) {
        const url = `${base}.${ext}`
        const ok = (await tryHead(url)) || (await tryByte(url))
        if (ok) {
          if (dead) return
          setSrc(url)
          // независимо от расширения – трактуем как img
          setKind('img')
          return
        }
      }
    }

    pick()
    return () => { dead = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, exts.join(',')]) // ← это место было подсвечено; см. вариант ниже

  // ── Вариант без предупреждения (если хотите совсем убрать хинт eslint):
  // const extsKey = Array.isArray(exts) ? exts.join(',') : ''
  // useEffect(..., [base, extsKey])
  return (
    <PremiumImageLoadFrame
      theme={previewTheme}
      reserveWidth={previewWidth}
      reserveHeight={previewHeight}
      radius="inherit"
      resetKey={src || base}
    >
      {({ onLoad, onError }) => src ? (
        <Image
          className={className}
          src={src}
          alt={alt}
          sizes="100vw"
          width={previewWidth || 0}
          height={previewHeight || 0}
          loading="lazy"
          {...imgProps}
          onLoad={onLoad}
          onError={onError}
          style={{ ...(imgProps?.style || {}), width: '100%', height: 'auto' }}
        />
      ) : null}
    </PremiumImageLoadFrame>
  )
}

function publishMutedBloombergPlayback(frame) {
  if (typeof window === 'undefined') return

  try { window.__FORUM_MEDIA_MUTED__ = true } catch {}
  try { window.__SITE_MEDIA_MUTED__ = true } catch {}
  try { window.__FORUM_MEDIA_SOUND_UNLOCKED__ = false } catch {}
  try { window.__SITE_MEDIA_SOUND_UNLOCKED__ = false } catch {}
  try { window.__FORUM_MEDIA_SOUND_USER_SET__ = true } catch {}
  try { window.__SITE_MEDIA_SOUND_USER_SET__ = true } catch {}

  try {
    const root = document.documentElement
    root.dataset.forumMediaMuted = '1'
    root.dataset.mediaMuted = '1'
    root.dataset.forumMediaSoundUnlocked = '0'
    root.dataset.forumMediaSoundUserSet = '1'
  } catch {}

  try {
    window.dispatchEvent(new CustomEvent(MEDIA_MUTED_EVENT, {
      detail: {
        muted: true,
        id: HOME_BLOOMBERG_MEDIA_ID,
        source: HOME_BLOOMBERG_MEDIA_ID,
        hideBgAudio: true,
      },
    }))
  } catch {}

  try {
    window.dispatchEvent(new CustomEvent(SITE_MEDIA_PLAY_EVENT, {
      detail: {
        source: HOME_BLOOMBERG_MEDIA_ID,
        element: frame || null,
        id: HOME_BLOOMBERG_MEDIA_ID,
        manual: false,
        muted: true,
        audible: false,
      },
    }))
  } catch {}
}

function ResponsiveEmbed({ src, title = '', mediaId = '' }) {
  const hostRef = useRef(null)
  const viewportActiveRef = useRef(false)
  const playbackPublishedRef = useRef(false)
  const [isInViewport, setIsInViewport] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host || !src) return undefined

    let disposed = false
    let fallbackFrame = 0

    const commitVisibility = (nextVisible) => {
      if (disposed) return
      const next = Boolean(nextVisible)
      viewportActiveRef.current = next
      if (!next) playbackPublishedRef.current = false
      setIsInViewport((previous) => previous === next ? previous : next)
    }

    if (typeof window.IntersectionObserver === 'function') {
      const observer = new window.IntersectionObserver(([entry]) => {
        commitVisibility(Boolean(entry?.isIntersecting))
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      })

      observer.observe(host)

      return () => {
        disposed = true
        viewportActiveRef.current = false
        playbackPublishedRef.current = false
        observer.disconnect()
      }
    }

    // Narrow fallback for old WebViews without IntersectionObserver.
    const measure = () => {
      fallbackFrame = 0
      const rect = host.getBoundingClientRect()
      commitVisibility(
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth,
      )
    }
    const scheduleMeasure = () => {
      if (fallbackFrame) return
      fallbackFrame = window.requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('scroll', scheduleMeasure, { passive: true })
    window.addEventListener('resize', scheduleMeasure, { passive: true })

    return () => {
      disposed = true
      viewportActiveRef.current = false
      playbackPublishedRef.current = false
      if (fallbackFrame) window.cancelAnimationFrame(fallbackFrame)
      window.removeEventListener('scroll', scheduleMeasure)
      window.removeEventListener('resize', scheduleMeasure)
    }
  }, [src])

  const handleFrameLoad = useCallback((event) => {
    if (mediaId !== HOME_BLOOMBERG_MEDIA_ID) return
    if (!viewportActiveRef.current) return
    if (playbackPublishedRef.current) return

    playbackPublishedRef.current = true
    publishMutedBloombergPlayback(event.currentTarget)
  }, [mediaId])

  if (!src) return null
  return (
    <div
      ref={hostRef}
      className="embed-wrap"
      data-media-id={mediaId || undefined}
      data-viewport-mounted={isInViewport ? '1' : '0'}
    >
      {isInViewport ? (
        <iframe
          src={src}
          title={title}
          allow="autoplay 'none'; encrypted-media; picture-in-picture"
          allowFullScreen
          loading="eager"
          referrerPolicy="no-referrer-when-downgrade"
          data-muted="true"
          onLoad={handleFrameLoad}
        />
      ) : (
        <span className="embed-placeholder" aria-hidden="true" />
      )}
      <style jsx>{`
        .embed-wrap {
          position: relative;
          width: 100%;
          padding-top: 56.25%;
          border-radius: 12px;
          overflow: hidden;
        }
        .embed-placeholder {
          position: absolute;
          inset: 0;
          display: block;
        }
        .embed-wrap :global(iframe) {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
          display: block;
        }
      `}</style>
    </div>
  )
}

function readCookie(name) {
  try {
    const m = document.cookie.match(
      new RegExp(
        '(?:^|; )' +
          name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') +
          '=([^;]*)'
      )
    )
    return m ? decodeURIComponent(m[1]) : null
  } catch {
    return null
  }
}

function readAccountId() {
  try {
    if (typeof window === 'undefined') return null

    if (window.__AUTH_ACCOUNT__) {
      return String(window.__AUTH_ACCOUNT__)
    }

    const a1 = localStorage.getItem('asherId')
    const a2 = localStorage.getItem('ql7_uid')
    const a3 =
      localStorage.getItem('ql7_account') ||
      localStorage.getItem('account') ||
      localStorage.getItem('wallet')

    const c1 = readCookie('asherId')

    return a1 || a2 || a3 || c1
      ? String(a1 || a2 || a3 || c1)
      : null
  } catch {
    return null
  }
}

export default function Home() {
  const { t, lang } = useI18n()

const handleOpenQuantumWallet = () => {
  try {
    const accountId = readAccountId()

    window.dispatchEvent(
      new CustomEvent('quantum-wallet:open', {
        detail: {
          accountId,
          userKey: accountId,
        },
      })
    )
  } catch {}
}

  // === TG link status for hero button ===
  const [tgLinked, setTgLinked] = useState(false)

  // === слушаем статус связки, который сообщает AuthNavClient ===
  useEffect(() => {
    if (typeof window === 'undefined') return

    const onStatus = (ev) => {
      try { setTgLinked(!!ev?.detail?.linked) } catch {}
    }
    window.addEventListener('tg:link-status', onStatus)

    // если AuthNavClient уже положил кэш-статус — заберём сразу
    try {
      const get = window.__QL7_TG_LINK_GET__
      if (typeof get === 'function') setTgLinked(!!get())
    } catch {}

    // попросим AuthNavClient обновить статус (когда он появится)
    let tries = 0
    const tmr = setInterval(() => {
      tries += 1
      const refresh = window.__QL7_TG_LINK_REFRESH__
      if (typeof refresh === 'function') {
        try {
          const result = refresh()
          if (result && typeof result.then === 'function') {
            result.then((linked) => setTgLinked(!!linked)).catch(() => {})
          } else {
            setTgLinked(!!result)
          }
        } catch {}
        clearInterval(tmr)
      }
      if (tries > 25) clearInterval(tmr) // ~2.5 сек
    }, 100)

    return () => {
      window.removeEventListener('tg:link-status', onStatus)
      clearInterval(tmr)
    }
  }, [])

  const premiumValue = t('home_premium')
  const premium = premiumValue && typeof premiumValue === 'object' && !Array.isArray(premiumValue)
    ? premiumValue
    : {}
  const routes = Array.isArray(premium.routes) ? premium.routes : []
  const analysisSuiteValue = premium.analysisSuite
  const analysisSuite = analysisSuiteValue && typeof analysisSuiteValue === 'object' && !Array.isArray(analysisSuiteValue)
    ? analysisSuiteValue
    : {}
  const analysisSuiteSteps = Array.isArray(analysisSuite.steps) ? analysisSuite.steps : []
  const operationsValue = t('home_operations')
  const operations = operationsValue && typeof operationsValue === 'object' && !Array.isArray(operationsValue)
    ? operationsValue
    : {}
  const operationCopy = operations.modules && typeof operations.modules === 'object'
    ? operations.modules
    : {}
  const operationModules = HOME_OPERATION_MODULES
    .map((module) => ({ ...module, ...(operationCopy[module.id] || {}) }))
    .filter((module) => String(module.title || '').trim())
  const direction = lang === 'ar' ? 'rtl' : 'ltr'

  const handleOpenMetaMarket = () => {
    if (typeof window === 'undefined') return

    void runAuthorizedClientAction({
      actionKey: 'home-console-metamarket-open',
      source: 'home-console',
      action: (accountId) => {
        window.dispatchEvent(new CustomEvent('metamarket:open', {
          detail: {
            source: 'home-console',
            initialMode: 'market',
            giftFlow: false,
            viewerId: accountId,
          },
        }))
      },
    })
  }

  return (
    <div className="home-page center-wrap" lang={lang} dir={direction} data-ql7-render-managed="1" data-ql7-visual-scope="route">
      <main className="home-premium-shell">
        <section className="home-hero-panel" data-ql7-render-managed="1" data-ql7-visual-scope="panel">
          <div className="home-hero-copy">
            <p className="home-kicker">{premium.kicker}</p>
            <h1>{t('hero_title')}</h1>
          </div>

          <figure className="home-hero-media">
            <AutoMedia
              base="/branding/telegram_card_tape_fixed"
              // явно: только картинки, без видео
              exts={['jpg', 'png', 'webp']}
              className="tg-tape"
              alt={premium.heroAlt || ''}
              previewWidth={1536}
              previewHeight={865}
            />
            <span className="home-media-frame" aria-hidden="true" />
          </figure>

          <div className="home-hero-context">
            <p className="home-hero-lead">{premium.lead}</p>
            <div className="home-status-rail" aria-label={premium.statusLabel}>
              {(Array.isArray(premium.statuses) ? premium.statuses : []).map((status, index) => (
                <span key={`${index}-${status}`}><i aria-hidden="true" />{status}</span>
              ))}
            </div>
          </div>

          <div className="home-entry-deck">
            <div className="home-entry-copy">
              <strong>{premium.entryTitle}</strong>
              <span>{premium.entryBody}</span>
            </div>
            <div className="row home-entry-actions">
          {/* заменили только эту кнопку */}
          <button
            type="button"
            className={`btn ${tgLinked ? 'is-linked' : ''}`}
            disabled={tgLinked}
            aria-disabled={tgLinked ? 'true' : 'false'}
            onClick={() => {
              // если уже связано — связку НЕ запускаем,
              // ведём на бота как и раньше
              if (tgLinked) {
                return
              }

              // если не связано — триггерим связку из AuthNavClient
              const fn = typeof window !== 'undefined' && window.__QL7_TG_LINK_START__
              if (typeof fn === 'function') fn()
            }}
          >
            {tgLinked ? t('tg_link_done') : t('tg_link_cta')}
          </button>

<QuantumWalletLaunchButton
  onClick={handleOpenQuantumWallet}
  title="Open Quantum Wallet"
/>
            </div>
          </div>
        </section>

        <section className="home-live-panel" key="live-bloomberg" data-ql7-render-managed="1" data-ql7-visual-scope="panel">
          <header className="home-section-heading">
            <span className="home-section-index" aria-hidden="true">01</span>
            <div>
              <p>{premium.liveEyebrow}</p>
              <h2>{premium.liveTitle}</h2>
              <span>{premium.liveBody}</span>
            </div>
          </header>

          <div className="home-live-stage">
            <CryptoNewsLens statusLabel={analysisSuite.status} />

            <ResponsiveEmbed
              src={HOME_BLOOMBERG_LIVE_URL}
              title="Bloomberg US — Live"
              mediaId={HOME_BLOOMBERG_MEDIA_ID}
            />
          </div>
        </section>

        <section
          className="home-signal-panel"
          aria-labelledby="home-signal-title"
          data-ql7-render-managed="1"
          data-ql7-visual-scope="panel"
        >
          <header className="home-signal-header">
            <span className="home-signal-index" aria-hidden="true">QL7</span>
            <div className="home-signal-heading-copy">
              <p>{analysisSuite.eyebrow}</p>
              <h2 id="home-signal-title">{analysisSuite.title}</h2>
              <span>{analysisSuite.body}</span>
            </div>
          </header>

          <ol className="home-signal-steps">
            {analysisSuiteSteps.map((step, index) => (
              <li key={`${step.code || index}-${step.title || index}`}>
                <span aria-hidden="true">{step.code || String(index + 1).padStart(2, '0')}</span>
                <div>
                  <strong>{step.title}</strong>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <footer className="home-signal-note">
            <strong>{analysisSuite.noteTitle}</strong>
            <span>{analysisSuite.note}</span>
          </footer>
        </section>

        {/* Реклама остаётся между live-контуром и информационной картой главной. */}
        <HomeBetweenBlocksAd key="home-ad-between-hero-and-block-1" />

        <section className="home-ops-panel" aria-labelledby="home-ops-title" data-ql7-render-managed="1" data-ql7-visual-scope="panel">
          <header className="home-ops-heading">
            <span className="home-section-index" aria-hidden="true">02</span>
            <div className="home-ops-heading-copy">
              <p>{operations.kicker}</p>
              <h2 id="home-ops-title">{operations.title}</h2>
              <span>{operations.lead}</span>
            </div>
            <div className="home-ops-telemetry" aria-label={operations.statusLabel}>
              <p>{operations.statusLabel}</p>
              <div>
                <span data-state="live"><i aria-hidden="true" /><strong>{operations.liveLabel}</strong><small>{operations.liveMeaning}</small></span>
                <span data-state="partial"><i aria-hidden="true" /><strong>{operations.partialLabel}</strong><small>{operations.partialMeaning}</small></span>
                <span data-state="roadmap"><i aria-hidden="true" /><strong>{operations.roadmapLabel}</strong><small>{operations.roadmapMeaning}</small></span>
                <span data-state="longterm"><i aria-hidden="true" /><strong>{operations.longtermLabel}</strong><small>{operations.longtermMeaning}</small></span>
              </div>
            </div>
          </header>

          <div className="home-ops-grid">
            {operationModules.map((module, index) => {
              const instructions = Array.isArray(module.instructions) ? module.instructions : []
              const launchLabel = module.state === 'partial'
                ? (operations.partialLaunchLabel || operations.launchLabel)
                : module.state === 'roadmap'
                  ? (operations.roadmapLaunchLabel || operations.launchLabel)
                  : operations.launchLabel
              const launcher = module.action === 'metamarket'
                ? (
                  <button type="button" className="home-ops-launch" onClick={handleOpenMetaMarket}>
                    <span>{launchLabel}</span>
                    <i aria-hidden="true">↗</i>
                  </button>
                )
                : module.href
                  ? (
                    <Link className="home-ops-launch" href={module.href}>
                      <span>{launchLabel}</span>
                      <i aria-hidden="true">↗</i>
                    </Link>
                  )
                  : null

              return (
                <article className="home-ops-card" data-state={module.state} key={module.id}>
                  {module.state !== 'live' && (
                    <span className="home-ops-ribbon">
                      {module.state === 'partial' ? (
                        <b>{operations.partialMark || 'PARTIAL'}</b>
                      ) : (
                        <b>{operations.soonMark || 'SOON'}</b>
                      )}
                    </span>
                  )}
                  <header className="home-ops-card-heading">
                    <span className="home-ops-code" aria-hidden="true">{module.code}</span>
                    <div>
                      <p>{module.eyebrow}</p>
                      <h3>{module.title}</h3>
                    </div>
                    {module.state === 'live' && (
                      <span className="home-ops-state" data-state="live">
                        <i aria-hidden="true" />
                        <span>{operations.liveLabel}</span>
                      </span>
                    )}
                  </header>

                  <p className="home-ops-summary">{module.summary}</p>

                  <div className="home-ops-value">
                    <span>{operations.valueLabel}</span>
                    <p>{module.value}</p>
                  </div>

                  {instructions.length > 0 && (
                    <details className="home-ops-detail">
                      <summary>
                        <span>{module.detailTitle || operations.detailsLabel}</span>
                        <i aria-hidden="true">+</i>
                      </summary>
                      <ol>
                        {instructions.map((instruction, stepIndex) => (
                          <li key={`${module.id}-${stepIndex}`}>
                            <span aria-hidden="true">{String(stepIndex + 1).padStart(2, '0')}</span>
                            <p>{instruction}</p>
                          </li>
                        ))}
                      </ol>
                    </details>
                  )}

                  <footer>
                    <span>{String(index + 1).padStart(2, '0')} / {String(operationModules.length).padStart(2, '0')}</span>
                    {launcher || (
                      <span className="home-ops-soon-lock">
                        {module.state === 'longterm'
                          ? (operations.longtermNotice || operations.soonNotice)
                          : (operations.roadmapNotice || operations.soonNotice)}
                      </span>
                    )}
                  </footer>
                </article>
              )
            })}
          </div>
        </section>

        <figure className="home-lab-panel">
          <AutoMedia
            base="/branding/qc_room"
            exts={['jpg']}
            className="quantum-shot"
            alt={premium.labAlt || ''}
            previewWidth={1536}
            previewHeight={724}
            imgProps={{}}
            videoProps={{}}
          />
          <figcaption>
            <span>{premium.labEyebrow}</span>
            <strong>{premium.labTitle}</strong>
            <p>{premium.labBody}</p>
          </figcaption>
        </figure>

        <nav className="home-route-map home-route-map--stacked" aria-label={premium.mapLabel} data-home-route-layout="premium-v2">
          <div className="home-route-map-copy">
            <span>{premium.mapLabel}</span>
            <p>{premium.mapHint}</p>
          </div>
          <div className="home-route-map-links home-route-map-links--single-line">
            {routes.map((route, index) => {
              const number = String(index + 3).padStart(2, '0')
              const label = String(route?.shortTitle || route?.title || '').trim()
              const labelLength = Math.max(1, Array.from(label).length)
              const labelFit = Math.max(6, Math.min(18, 94 / labelLength)).toFixed(2)
              return (
                <a
                  href={`#home-route-${index + 1}`}
                  key={`${number}-${route?.title || ''}`}
                  data-label-length={labelLength}
                  style={{ '--home-route-label-fit': labelFit }}
                >
                  <span aria-hidden="true">{number}</span>
                  <strong>{label}</strong>
                </a>
              )
            })}
          </div>
        </nav>

        <div className="home-route-canvas" aria-label={premium.mapLabel}>
          {routes.map((route, index) => {
            const number = String(index + 3).padStart(2, '0')
            const details = Array.isArray(route?.details) ? route.details : []

            return (
              <section className="home-route-block" id={`home-route-${index + 1}`} key={`${number}-${route?.title || ''}`}>
                <header className="home-route-header">
                  <span className="home-route-index" aria-hidden="true">{number}</span>
                  <div>
                    <p>{route?.status || ''}</p>
                    <h2>{route?.title || ''}</h2>
                  </div>
                  <span className="home-route-count">{details.length} {premium.detailCountLabel}</span>
                </header>

                <div className="home-route-body">
                  <p className="home-route-summary">{route?.summary || ''}</p>
                  {details.length > 0 && (
                    <div className="home-route-details">
                      {details.map((detail, detailIndex) => (
                        <details className="home-route-detail" key={`${number}-${detailIndex}-${detail?.title || ''}`}>
                          <summary>
                            <span aria-hidden="true">{String(detailIndex + 1).padStart(2, '0')}</span>
                            <strong>{detail?.title || ''}</strong>
                            <i aria-hidden="true">+</i>
                          </summary>
                          <p>{detail?.body || ''}</p>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )
          })}
        </div>
      </main>

      <FooterViewportPark scope="home-footer-marquee" reserveMarginTopPx={10}>
        <FooterMarqueeTail renderManaged />
      </FooterViewportPark>

      {/* ← заменено <img> на <Image/> в двух иконках */}
      <FooterViewportPark scope="home-footer-actions">
        <div className="icons-row ql7-footer-social-layout" data-ql7-render-managed="1" data-ql7-visual-scope="panel">
        <Link
          href="/privacy"
          className="ql7-icon-link ql7-footer-policy-link"
          aria-label="Privacy / Политика"
          style={{ '--size': '130px' }}
        >
          <Image
            className="ql7-click-icon"
            src="/click/policy.png"
            alt="Privacy"
            width={130}
            height={130}
            draggable={false}
          />
        </Link>

        <SiteSocialLinksRow className="ql7-social-links-row--footer" />

        <Link
          href="/forum?ql7SupportOpen=1&inbox=messages&dmUser=ql7-support"
          className="ql7-icon-link ql7-footer-support-link"
          data-ql7-support-entry="1"
          aria-label="Support / Поддержка"
          style={{ '--size': '130px' }}
        >
          <Image
            className="ql7-click-icon"
            src="/click/support.png"
            alt="Support"
            width={130}
            height={130}
            draggable={false}
          />
        </Link>
        </div>
      </FooterViewportPark>

      {/* ✅ стили ВОЗВРАЩЕНЫ 1:1 как у тебя */}
      <style jsx>{`
        .row { display:flex; justify-content:center; gap:12px; flex-wrap:wrap; }
        .btn.is-linked, .btn:disabled {
          opacity:.56;
          cursor:not-allowed;
          filter:saturate(.65) brightness(.85);
          box-shadow:none;
        }
        .live-grid { display:grid; grid-template-columns:1fr; gap:16px; margin-top:12px; }
        .live-col h3 { margin:0 0 8px 0; font-weight:600; }
        @media (min-width:720px){ .live-grid { grid-template-columns:1fr 1fr; } }

        .icons-row{
          width:100%; display:flex; align-items:center; justify-content:space-evenly;
          gap:24px; flex-wrap:wrap; padding:16px 0 8px;
        }
        .icon-link{ display:inline-block; line-height:0; cursor:pointer; outline:none; }
        .click-icon{
          width:var(--size, 120px); height:auto; display:block; user-select:none;
          pointer-events:none; background:transparent;
          animation: floatY 3s ease-in-out infinite, glow 2.4s ease-in-out infinite alternate;
        }
        .icon-link:hover .click-icon, .icon-link:focus-visible .click-icon{
          animation: floatY 3s ease-in-out infinite, glow-strong 1.6s ease-in-out infinite alternate, bounce .8s cubic-bezier(.22,1,.36,1);
        }
        @keyframes floatY{0%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-8px) rotate(-2deg)}100%{transform:translateY(0) rotate(0deg)}}
        @keyframes glow{0%{filter:drop-shadow(0 2px 6px rgba(0,200,255,.18))}100%{filter:drop-shadow(0 10px 22px rgba(0,200,255,.45))}}
        @keyframes glow-strong{0%{filter:drop-shadow(0 4px 10px rgba(0,200,255,.28))}100%{filter:drop-shadow(0 14px 32px rgba(0,220,255,.7))}}
        @keyframes bounce{0%{transform:translateY(0) scale(1)}35%{transform:translateY(-16px) scale(1.06)}60%{transform:translateY(0) scale(.98)}85%{transform:translateY(-8px) scale(1.03)}100%{transform:translateY(0) scale(1)}
        }
        @media (prefers-reduced-motion:reduce){
          .click-icon{ animation:none; }
          .icon-link:hover .click-icon, .icon-link:focus-visible .click-icon{ animation:none; }
        }
      `}</style>
    </div>
  )
}
